// Lightweight SSE wrapper for /v1/runs/:id/events/stream.
//
// EventSource doesn't accept custom headers, so we can't send Authorization
// on the request line. The cortex API has to either accept the JWT via a
// query string OR use a session cookie set after Keycloak login. This
// implementation appends `?access_token=<jwt>` and falls through to a no-op
// if construction fails — the caller already polls via react-query as a
// belt-and-braces fallback.
//

// Confirmed via Swagger: GET /v1/runs/:id/events/stream — content-type
// text/event-stream, replays stored events then tails Redis pubsub
// cortex:run:{id}.
export function subscribeRunEvents(
  baseUrl: string,
  runId: string,
  token: string | null,
  onEvent: (data: unknown) => void,
): () => void {
  const url = new URL(`${baseUrl.replace(/\/$/, '')}/v1/runs/${runId}/events/stream`);
  if (token) url.searchParams.set('access_token', token);

  let es: EventSource | null = null;
  try {
    es = new EventSource(url.toString(), { withCredentials: true });
  } catch (err: unknown) {
    console.warn('console/sse:EventSource', err);
    return () => {};
  }
  const handle = (e: MessageEvent) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch (err: unknown) {
      console.info('console/sse:parseMessage', err);
      onEvent(e.data);
    }
  };
  es.addEventListener('message', handle);
  es.addEventListener('error', () => {
    // Persistent error → close so the browser doesn't keep retrying. React
    // Query polling already covers the gap.
    es?.close();
  });
  return () => {
    es?.removeEventListener('message', handle);
    es?.close();
  };
}

/**
 * Subscribe to a persistent-agent session's watch stream — the dashboard
 * (fact lanes) + doorbell (events) + transcript (chunks). Replays current
 * state on every (re)connect then tails the host's live frames. Same
 * EventSource token-on-query caveat as `subscribeRunEvents`. Frames are
 * PersistentAgentWatchFrame.
 *
 * Reconnects MANUALLY on error rather than relying on the browser's
 * built-in same-socket EventSource retry: that retry would keep reusing the
 * (possibly-expired) token captured in the URL at construction time, so a
 * Keycloak rotation would wedge the stream. Instead, on error we report the
 * outage via `onConnectionChange('reconnecting')`, tear the socket down, and
 * reopen after a short delay with a freshly read token (`getToken`). Because
 * the server replays full history on EVERY connection, `onReconnect` fires
 * on EVERY open — first open and every manual reconnect alike — so the
 * consumer resets replayed collections before that connection's replay
 * lands; skipping the reset on any open duplicates the transcript.
 *
 * Two additional mechanisms eliminate the "Reconnecting…" banner flash:
 *
 *   L2 — Proactive token refresh: the JWT's `exp` claim is parsed at open
 *   time and a timer fires 60 s before expiry to voluntarily reconnect with
 *   a fresh token. This prevents the browser's native retry from ever
 *   encountering a stale token and getting a 401 (CLOSED path).
 *
 *   L3 — CLOSED-only banner: `onConnectionChange('reconnecting')` fires
 *   only on hard failures (readyState CLOSED). For readyState CONNECTING
 *   the browser is already retrying; the consumer's existing 1,200 ms
 *   debounce still shows the banner if the reconnect takes too long.
 */
export function subscribePersistentAgentWatch(
  baseUrl: string,
  groupId: string,
  agentId: string,
  getToken: () => string | null,
  onFrame: (frame: unknown) => void,
  opts?: {
    onConnectionChange?: (state: 'live' | 'reconnecting') => void;
    /** Fired on every open, before that connection's replay — reset replayed state. */
    onReconnect?: () => void;
  },
): () => void {
  const streamUrl = `${baseUrl.replace(/\/$/, '')}/v1/persistent-agent-groups/${groupId}/agents/${agentId}/stream`;
  let es: EventSource | null = null;
  let closed = false;
  let reconnectTimer: number | null = null;
  let proactiveTimer: number | null = null;

  const handle = (e: MessageEvent) => {
    try {
      onFrame(JSON.parse(e.data));
    } catch (err: unknown) {
      console.info('console/sse:pa-watch-parse', err);
    }
  };
  const handleOpen = () => {
    // EVERY open precedes a full-history replay from the server, so the
    // consumer must reset replayed state each time. On a truly fresh mount the
    // reset is a no-op; on a reconnect over surviving state it prevents a
    // duplicated transcript.
    opts?.onReconnect?.();
    opts?.onConnectionChange?.('live');
  };
  const teardown = () => {
    if (!es) return;
    es.removeEventListener('message', handle);
    es.removeEventListener('open', handleOpen);
    es.removeEventListener('error', handleError);
    es.close();
    es = null;
  };
  const scheduleReconnect = () => {
    if (closed || reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      open();
    }, 1500);
  };
  const handleError = () => {
    // L3: only signal 'reconnecting' for hard failures (CLOSED). When
    // readyState is CONNECTING the browser is already retrying natively with
    // the same URL — the consumer's 1,200 ms debounce still surfaces the
    // banner if that retry takes too long, without a false-positive flash on
    // every transient blip that recovers in under a second.
    if (es && es.readyState === EventSource.CLOSED) {
      opts?.onConnectionChange?.('reconnecting');
      teardown();
      scheduleReconnect();
    }
  };
  const open = () => {
    if (closed) return;

    // Clear any pending proactive timer before opening a new connection.
    if (proactiveTimer !== null) {
      window.clearTimeout(proactiveTimer);
      proactiveTimer = null;
    }

    const url = new URL(streamUrl);
    const token = getToken();
    if (token) url.searchParams.set('access_token', token);

    // L2: schedule a voluntary reconnect 60 s before the token expires so
    // the browser's native retry never encounters a stale token (avoiding
    // the 401 → CLOSED → banner path). Uses only the standard `exp` claim;
    // no JWT library needed.
    const exp = parseJwtExp(token);
    if (exp !== null) {
      const msUntilRefresh = exp * 1000 - Date.now() - 60_000;
      if (msUntilRefresh > 0) {
        proactiveTimer = window.setTimeout(() => {
          proactiveTimer = null;
          teardown();
          open(); // fresh token read via getToken()
        }, msUntilRefresh);
      }
    }

    try {
      es = new EventSource(url.toString(), { withCredentials: true });
    } catch (err: unknown) {
      console.warn('console/sse:pa-watch', err);
      opts?.onConnectionChange?.('reconnecting');
      scheduleReconnect();
      return;
    }
    es.addEventListener('message', handle);
    es.addEventListener('open', handleOpen);
    es.addEventListener('error', handleError);
  };

  open();
  return () => {
    closed = true;
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (proactiveTimer !== null) {
      window.clearTimeout(proactiveTimer);
      proactiveTimer = null;
    }
    teardown();
  };
}

/** Parse the `exp` Unix timestamp from a JWT without a library. Returns null on any failure. */
function parseJwtExp(token: string | null): number | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as unknown;
    if (typeof payload !== 'object' || payload === null) return null;
    const exp = (payload as Record<string, unknown>)['exp'];
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to the org's persistent-agent INTERACTION stream — the realtime
 * "who is talking to whom" feed behind the graph panel. Snapshot-then-pulses:
 * every (re)open the server sends ONE `graph` frame (live threads, recent
 * users, edge aggregates) followed by `interaction` pulses. Same never-close +
 * reset-on-open contract as `subscribePersistentAgentWatch`: the consumer
 * resets on every open and the fresh snapshot repopulates it.
 */
export function subscribePersistentAgentInteractions(
  baseUrl: string,
  token: string | null,
  onFrame: (frame: unknown) => void,
  opts?: {
    onConnectionChange?: (state: 'live' | 'reconnecting') => void;
    /** Fired on every open, before that connection's snapshot — reset state. */
    onReconnect?: () => void;
  },
): () => void {
  const url = new URL(`${baseUrl.replace(/\/$/, '')}/v1/persistent-agents/interactions/stream`);
  if (token) url.searchParams.set('access_token', token);

  let es: EventSource | null = null;
  try {
    es = new EventSource(url.toString(), { withCredentials: true });
  } catch (err: unknown) {
    console.warn('console/sse:pa-interactions', err);
    opts?.onConnectionChange?.('reconnecting');
    return () => {};
  }
  const handle = (e: MessageEvent) => {
    try {
      onFrame(JSON.parse(e.data));
    } catch (err: unknown) {
      console.info('console/sse:pa-interactions-parse', err);
    }
  };
  const handleOpen = () => {
    opts?.onReconnect?.();
    opts?.onConnectionChange?.('live');
  };
  const handleError = () => {
    // Never close — EventSource retries and the server re-snapshots on open.
    opts?.onConnectionChange?.('reconnecting');
  };
  es.addEventListener('message', handle);
  es.addEventListener('open', handleOpen);
  es.addEventListener('error', handleError);
  return () => {
    es?.removeEventListener('message', handle);
    es?.removeEventListener('open', handleOpen);
    es?.removeEventListener('error', handleError);
    es?.close();
  };
}

/**
 * Org-wide SSE subscription (DEV-16319 / DEV-16233). Same EventSource
 * caveats as `subscribeRunEvents` — token rides on the query string, and
 * persistent errors close the connection so the browser doesn't busy-retry.
 * Live tap only: the caller is expected to bootstrap state via REST first
 * and then layer events on top.
 */
export function subscribeOrgEvents(
  baseUrl: string,
  token: string | null,
  onEvent: (data: unknown) => void,
): () => void {
  const url = new URL(`${baseUrl.replace(/\/$/, '')}/v1/events/stream`);
  if (token) url.searchParams.set('access_token', token);
  let es: EventSource | null = null;
  try {
    es = new EventSource(url.toString(), { withCredentials: true });
  } catch (err: unknown) {
    console.warn('console/sse:org-event-source', err);
    return () => {};
  }
  const handle = (e: MessageEvent) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch (err: unknown) {
      console.info('console/sse:org-parse-message', err);
      onEvent(e.data);
    }
  };
  es.addEventListener('message', handle);
  es.addEventListener('error', () => {
    es?.close();
  });
  return () => {
    es?.removeEventListener('message', handle);
    es?.close();
  };
}
